import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  debounce,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as None } from 'assets/images/ic_none.svg';
import { Formik } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { IncidentService } from 'services/IncidentService';
import DeleteIcon from '@mui/icons-material/Delete';
import { Toast, Utils } from 'shared/helpers';
import DateFnsAdapter from '@date-io/date-fns';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import useSize from 'shared/components/Hooks';

const dateFns = new DateFnsAdapter();

export interface IIncident {
  _id: string;
  title: string;
  description: string;
  incident_source: string;
  incident_type: string;
  severity: string;
  business_owner: string;
  contains_personal_data: boolean;
  assigned_to: any | { name: string };
  status: string;
  detected_at: string;
  data_subjects_impacted: number;
  dpo_notified_at: string;
  regulatory_notified_at: string;
  regulatory_notification_reference: string;
  internal_escalation_level: string;
  containment_actions: string;
  root_cause: string;
  root_cause_report: string;
  operational_disruption_level: string;
  corrective_actions: string;
  harm_description: string;
  compliance_impact: string;
  recurrence_risk: string;
  retest_results: string;
  lessons_learned: string;
  reported_by: any | { name: string };
  tenantId: any;
  updateNotes: any;
  isActive: boolean;
  deletedAt: any;
  createdAt: string;
  updatedAt: string;
  aisystemId: IAISystem[];
  datasetId: IDataset[];
  __v: number;
}

// const SearchFormikContext = ({ onChange }: any): any => {
//   const { values } = useFormikContext();

//   useEffect(() => {
//     onChange(values);
//   }, [values]);

//   return null;
// };

const IncidentsCard: React.FC<any> = () => {
  const location = useLocation();
  const { severity, incident_type } = location.state || {};

  console.log(severity, incident_type);
  const { isMobile } = useSize();

  const dispatch = useDispatch();
  const incidentsPagination = useSelector(
    (state: IAppState) => state.paginationState.incidents
  );

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<IIncident[]>([]);
  const [filteredIncident, setFilteredIncident] = useState<any>([]);
  const [deleteRecord, setDeleteRecord] = useState<IIncident>();
  const [openDeleteIncident, setOpenDeleteIncident] = useState<any>(null);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const loadRecords = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: String(incidentsPagination.offset),
      limit: String(incidentsPagination.pageLimit),
      title: incidentsPagination.search,
      status: incidentsPagination.status,
      severity: incidentsPagination.severity,
    }).toString();
    IncidentService.getAll(queryString)
      .then((response) => {
        const { incidents, pagination } = response.data.data;
        setIncidents(incidents);

        dispatch(
          PaginationActions.setPagination({
            incidents: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );

        let filtered = incidents;
        if (severity) {
          filtered = filtered.filter(
            (item: IIncident) => item.severity === severity
          );
        }
        if (incident_type) {
          filtered = filtered.filter(
            (item: IIncident) => item.incident_type === incident_type
          );
        }
        setFilteredIncident(filtered);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        incidents: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        incidents: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          incidents: {
            ...incidentsPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [incidentsPagination]
  );

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        incidents: {
          ...incidentsPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleTypeChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        incidents: {
          ...incidentsPagination,
          offset: 1,
          severity: statusValue,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        incidents: {
          offset: 1,
          search: '',
          status: '',
          severity: '',
        },
      })
    );
  };

  const deleteRecordAPI = (id: string, callback: any): void => {
    IncidentService.deleteRecord(id)
      .then(() => {
        callback(true);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnDelete = (incident: IIncident): void => {
    setDeleteRecord(incident);
    deleteRecordAPI(incident._id, (status: boolean) => {
      setDeleteRecord(undefined);
      if (status) {
        Toast.showSuccessMessage('The incident has been successfully deleted.');
        setOpenDeleteIncident(null);
        loadRecords();
      }
    });
  };

  const handleCloseDeleteIncident = (): void => {
    setOpenDeleteIncident(false);
  };

  // const handleFilterChange = (values: any): any => {
  //   const { 'search-programs': search, status, severities } = values;
  //   if (!incidents.length) return;
  //   const filtered: any = incidents.filter((item) => {
  //     const matchesSearch = search
  //       ? item.title.toLowerCase().includes(search.toLowerCase())
  //       : true;
  //     const matchesStatus = status ? item.status === status : true;
  //     const matchesTypes = severities ? item.severity === severities : true;

  //     return matchesSearch && matchesStatus && matchesTypes;
  //   });

  //   setFilteredIncident(filtered);
  // };

  // Hooks
  useEffect(() => {
    loadRecords();
  }, [
    incidentsPagination.offset,
    incidentsPagination.pageLimit,
    incidentsPagination.search,
    incidentsPagination.severity,
    incidentsPagination.status,
  ]);

  // UI Element
  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title="Incidents"
          action={
            orgACLConfig.incidents?.includes(
              'incidents.incidentsLog.addReportIncidents'
            ) && (
              <AppPrimaryButton
                title="Report incidents"
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
                onClick={() => navigate('/incidents-log/add')}
              />
            )
          }
        />
        <CardContent>
          <Stack className="incidents-content" direction="column" spacing={5}>
            <Formik
              initialValues={{
                'search-programs': incidentsPagination.search,
                status: incidentsPagination.status,
                severities: incidentsPagination.severity,
              }}
              enableReinitialize
              onSubmit={() => console.log('searchprograms')}
            >
              {({ resetForm }) => (
                <>
                  {/* <SearchFormikContext onChange={handleFilterChange} /> */}
                  <Grid container className="incidents-content-nav">
                    <Grid item sm={4} xs={12} spacing={2}>
                      <Box className="incidents-search">
                        <AppFormikTextField
                          inputProps={{
                            startAdornment: (
                              <SvgIcon
                                component={Search}
                                inheritViewBox
                                className="view-icon"
                                fontSize="inherit"
                                htmlColor="#76A81F"
                              />
                            ),
                          }}
                          outerLabel={false}
                          placeholder="Search incident.."
                          name="search-programs"
                          onChange={(e: any) => {
                            debouncSearch(e.target.value);
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item sm={8} xs={12}>
                      <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={2}
                      >
                        <AppFormikSelectWithKeys
                          name="status"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          placeholder="Filter Status"
                          notEmphasized
                          notSelectablePlaceholder={false}
                          titleKey="label"
                          valueKey="key"
                          items={[
                            { key: '', label: 'All Statuses' },
                            { key: 'Detected', label: 'Detected' },
                            { key: 'Investigating', label: 'Investigating' },
                            { key: 'Triaged', label: 'Triaged' },
                            { key: 'Assessed', label: 'Assessed' },
                            { key: 'Notified', label: 'Notified' },
                            { key: 'Analyzed', label: 'Analyzed' },
                            { key: 'Contained', label: 'Contained' },
                            { key: 'Corrected', label: 'Corrected' },
                            { key: 'Remediated', label: 'Remediated' },
                            { key: 'Closed', label: 'Closed' },
                          ]}
                          onChange={(e: any) => {
                            handleStatusChange(e.target.value);
                          }}
                        />
                        <AppFormikSelectWithKeys
                          name="severities"
                          className="rework-selection"
                          notched={false}
                          labelId="fix-selection-label"
                          testid="solicitor-dropdown"
                          MenuProps={{
                            className: 'menu-fix-selection single-fix-rework',
                          }}
                          placeholder="Filter severity"
                          notEmphasized
                          notSelectablePlaceholder={false}
                          titleKey="label"
                          valueKey="key"
                          items={[
                            { key: '', label: 'All Severities' },
                            { key: 'Critical', label: 'Critical' },
                            { key: 'High', label: 'High' },
                            { key: 'Medium', label: 'Medium' },
                            { key: 'Low', label: 'Low' },
                          ]}
                          onChange={(e: any) => {
                            handleTypeChange(e.target.value);
                          }}
                        />
                        <AppPrimaryButtonOutlined
                          fullWidth
                          title="Clear Filters"
                          onClick={() => {
                            resetForm();
                            handleClearFilter();
                          }}
                          sx={{
                            borderColor: '#e7e7e7 !important',
                            boxShadow: 'none !important',
                            background: 'white !important',
                            color: '#7e7e7e !important',
                            height: '1.4375em !important',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              )}
            </Formik>
            {isLoadingRecords && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}
            {!isLoadingRecords && (
              <TableContainer className="app-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Incident</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Assinged To</TableCell>
                      <TableCell>Detected</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIncident.length > 0 &&
                      filteredIncident.map((incident: any) => (
                        <TableRow key={incident._id}>
                          <TableCell>
                            {incident.title}
                            <Typography>{incident.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={incident.status} />
                          </TableCell>
                          <TableCell>
                            <Chip label={incident.severity} />
                          </TableCell>
                          <TableCell>
                            {incident.assigned_to
                              ? incident.assigned_to.name
                              : 'Not Assigned'}
                          </TableCell>
                          <TableCell>
                            {incident.detected_at
                              ? `${dateFns
                                  .date(incident.detected_at)
                                  .toLocaleDateString()} ${dateFns
                                  .date(incident.detected_at)
                                  .toLocaleTimeString()}`
                              : ''}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={2}>
                              {orgACLConfig.incidents?.includes(
                                'incidents.incidentsLog.viewReportIncidents'
                              ) && (
                                <AppPrimaryButtonOutlined
                                  className="primary-light-fill btn-extra-small"
                                  title="View"
                                  onClick={() => {
                                    navigate(`/incidents-log/${incident._id}`);
                                  }}
                                />
                              )}
                              {orgACLConfig.incidents?.includes(
                                'incidents.incidentsLog.deleteReportIncidents'
                              ) && (
                                <IconButton
                                  onClick={() => {
                                    setOpenDeleteIncident(incident);
                                  }}
                                >
                                  {deleteRecord?._id === incident._id && (
                                    <CircularProgress />
                                  )}
                                  {deleteRecord?._id !== incident._id && (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!isLoadingRecords && incidents.length > 0 && (
              <TablePagination
                component="div"
                count={incidentsPagination.totalRecords}
                page={incidentsPagination.offset - 1}
                rowsPerPage={incidentsPagination.pageLimit}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                disabled={isLoadingRecords}
              />
            )}

            {!isLoadingRecords && filteredIncident.length === 0 && (
              <EmptyPlaceholder
                icon={
                  <SvgIcon
                    component={None}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
                title="No incidents found."
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <ConfirmDialog
        title="Delete Incident"
        subTitle="Are you sure to delete the incident?"
        showConfirm={openDeleteIncident}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteIncident();
          actionOnDelete(openDeleteIncident);
        }}
        onCancel={handleCloseDeleteIncident}
        onCloseDialog={handleCloseDeleteIncident}
      />
    </>
  );
};

export default IncidentsCard;
